import React from 'react';
import PropTypes from 'prop-types';
import { Prompt, matchPath } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import './PreventTransitionDialog.scss';

const makeKey = i => i.toString();

const DefaultDialog = ({ title, message, onLeave, onStay, open }) => (
  <Dialog open={open} onClose={onStay} className="grb-prevent-transition-dialog" id="preventTransitionDialog">
    <DialogTitle>{title}</DialogTitle>
    <DialogContent className="grb-prevent-transition-dialog-content">
      {(Array.isArray(message) && message || [message]).map((msg, i) => (
        <DialogContentText key={makeKey(i)}>{msg}</DialogContentText>
      ))}
    </DialogContent>
    <DialogActions>
      <Button onClick={onLeave} variant="outlined" id="leavePage">
        Leave
      </Button>
      <Button onClick={onStay} color="primary" variant="contained" autoFocus id="stayOnPage">
        Stay
      </Button>
    </DialogActions>
  </Dialog>
);

DefaultDialog.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  onLeave: PropTypes.func.isRequired,
  onStay: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

// See https://codesandbox.io/s/myw173jyq8?file=/src/PreventTransitionPrompt.js:435-3054
class PreventTransitionDialog extends React.Component {
  constructor() {
    super();

    this.state = { open: false };

    this.show = this.show.bind(this);
    this.handleTransition = this.handleTransition.bind(this);
    this.handleLeave = this.handleLeave.bind(this);
    this.handleStay = this.handleStay.bind(this);
  }

  componentDidMount() {
    window[this.props.dialogKey] = this.show;
  }

  componentWillUnmount() {
    delete window[this.props.dialogKey];
  }

  /**
   * Show the dialog. Invoked primarily from React Router transition
   * handler getUserConfirmation.
   *
   * @param allowTransitionCallback A function that accepts a flag whether or not to allow the route transition
   */
  show(transitionCallback) {
    // we are immediately preventing any transitions here
    // but could just as easily base this off a user interaction
    // or other state
    // this.setState({ open: true, transitionCallback }, () => allowTransitionCallback(false));
    this.setState({ open: true, transitionCallback });
  }

  handleLeave() {
    this.setState({ open: false });
    this.state.transitionCallback(true);
  }

  handleStay() {
    this.setState({ open: false });
    this.state.transitionCallback(false);
  }

  handleTransition(location) {
    const { route } = this.props;
    // If current route matches the new location, no need to prompt a dialog,
    // we will remain on the same page => no state loss
    if (route) {
      const { matchPath: routeMatchPath } = route;
      const p = (Array.isArray(routeMatchPath) ? routeMatchPath : [routeMatchPath]).find((path) => {
        const r = matchPath(location.pathname, { path });
        return r;
      });
      if (p) {
        // The current route matches the new location
        return true;
      }
    }
    return this.props.dialogKey;
  }

  render() {
    const { when, title, message, Component } = this.props;
    const { open } = this.state;

    return (
      <React.Fragment>
        {/* React Router prompt, callback will return true to allow transition or dialog key to prevent */}
        <Prompt when={when} message={this.handleTransition} />

        {/* Example MUI dialog to show when open. You could make this
            totally customizable or a complete one-off. */}
        {open && (
          <Component
            title={title}
            message={message}
            onLeave={this.handleLeave}
            onStay={this.handleStay}
            open={open}
          />
        )}
      </React.Fragment>
    );
  }
}

PreventTransitionDialog.propTypes = {
  when: PropTypes.bool.isRequired,
  title: PropTypes.string,
  message: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  dialogKey: PropTypes.string.isRequired,
  Component: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({}),
  ]),
  route: PropTypes.shape({
    matchPath: PropTypes.string,
  }),
};

PreventTransitionDialog.defaultProps = {
  title: 'Unsaved Changes',
  message: [
    'You have unsaved changes that will be lost when navigating away.',
    'Are you sure you want to leave this page?',
  ],
  Component: DefaultDialog,
  route: undefined,
};

export default PreventTransitionDialog;
